/**
 * Banque › Compte d'attente (499000) : la file des mouvements dont une part
 * n'a pas encore sa place au grand livre.
 *
 * Une file de problèmes à résoudre, pas un cimetière (orientation d'Éclosion
 * du 28/09/2026) : chaque ligne dit combien attend, pourquoi, depuis quand, et
 * si un humain l'y a mis (un reste non affecté ou un classement « Reconnu à
 * la forme » de l'import n'est la décision de personne).
 *
 * Lecture seule. Imputer une ligne ailleurs (versement Stripe, crédit d'une
 * autre activité, dépôt, fournisseur) est le lot T2 ; d'ici on retrouve le
 * mouvement et son écriture de banque.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ArrowRight, Hourglass, Loader2, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { messageErreur } from '../../../lib/payments/erreur';
import { ficheEcriture } from '../../../lib/navigation/chemins';
import {
  chargerLaFileDAttente, lireLEtatDeLaTresorerie, TAILLE_DE_PAGE,
} from '../../../lib/compta/tresorerie/gateway.supabase';
import {
  FILTRES_D_ATTENTE_VIDES, lireFiltresAttente, raisonAffichee, RAISONS_D_ATTENTE, SENS,
  versParametresAttente, type EtatDeLaTresorerie, type FiltresAttente, type MouvementEnAttente, type Sens,
} from '../../../lib/compta/tresorerie/types';
import { dateCourte, euros } from '../components/money';

export default function CompteDAttentePage() {
  const [parametres, setParametres] = useSearchParams();
  const [filtres, setFiltres] = useState<FiltresAttente>(() => lireFiltresAttente(parametres));
  const [page, setPage] = useState(0);

  const [lignes, setLignes] = useState<MouvementEnAttente[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [etat, setEtat] = useState<EtatDeLaTresorerie | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  /* Une adresse qui change sans remonter la page (le menu) porte d'autres filtres : la liste les suit. */
  useEffect(() => {
    const lus = lireFiltresAttente(parametres);
    if (versParametresAttente(lus).toString() === versParametresAttente(filtres).toString()) return;
    setFiltres(lus);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seule l'adresse déclenche ; `filtres` n'est que comparé
  }, [parametres]);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const [file, synthese] = await Promise.all([
        chargerLaFileDAttente(filtres, page * TAILLE_DE_PAGE),
        lireLEtatDeLaTresorerie(),
      ]);
      setLignes(file.lignes);
      setTotal(file.total);
      setEtat(synthese);
    } catch (e) {
      setErreur(messageErreur(e, 'Lecture du compte d’attente impossible'));
    } finally {
      setChargement(false);
    }
  }, [filtres, page]);

  useEffect(() => { void charger(); }, [charger]);

  const changer = (modif: Partial<FiltresAttente>) => {
    const suivants = { ...filtres, ...modif };
    setFiltres(suivants);
    setPage(0);
    setParametres(versParametresAttente(suivants), { replace: true });
  };

  const pages = total === null ? 1 : Math.max(1, Math.ceil(total / TAILLE_DE_PAGE));
  const filtresPoses = versParametresAttente(filtres).toString() !== '';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Hourglass className="h-6 w-6 text-primary-600" /> Compte d’attente (499000)
          </h1>
          <p className="max-w-3xl text-gray-600">
            Les mouvements bancaires dont une part n’a pas encore sa place au grand livre : hors périmètre, non
            affectée, ou affectée à ce que la comptabilité ne peut pas porter tel quel. Lecture seule ; l’imputation
            viendra au lot T2.
          </p>
        </div>
        <button type="button" onClick={charger} disabled={chargement} className="btn-primary flex items-center">
          <RefreshCw className={clsx('mr-2 h-4 w-4', chargement && 'animate-spin')} />
          Actualiser
        </button>
      </div>

      {etat && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(['entree', 'sortie'] as Sens[]).map((sens) => (
            <div key={sens} className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{SENS[sens]} en attente</p>
              <p className="text-xl font-semibold tabular-nums">{euros(etat.attente[sens].montantCents)}</p>
              <p className="text-sm text-gray-600">
                {etat.attente[sens].nombre.toLocaleString('fr-BE')} mouvement{etat.attente[sens].nombre > 1 ? 's' : ''}
              </p>
              <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
                {etat.parRaison.filter((r) => r.sens === sens).map((r) => (
                  <li key={r.raison} className="flex justify-between gap-2">
                    <button type="button" className="text-left hover:underline"
                            onClick={() => changer({ sens, raison: r.raison })}>
                      {raisonAffichee(r.raison)} ({r.nombre})
                    </button>
                    <span className="tabular-nums">{euros(r.montantCents)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select className="form-input w-auto py-2" aria-label="Sens" value={filtres.sens ?? ''}
                onChange={(e) => changer({ sens: (e.target.value || null) as Sens | null })}>
          <option value="">Entrées et sorties</option>
          <option value="entree">Entrées</option>
          <option value="sortie">Sorties</option>
        </select>
        <select className="form-input w-auto py-2" aria-label="Raison" value={filtres.raison ?? ''}
                onChange={(e) => changer({ raison: e.target.value || null })}>
          <option value="">Toutes les raisons</option>
          {Object.entries(RAISONS_D_ATTENTE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={filtres.aRegarder} onChange={(e) => changer({ aRegarder: e.target.checked })} />
          Seulement ce qu’aucun humain n’a encore tranché
        </label>
        {filtresPoses && (
          <button type="button" className="text-sm text-primary-700 hover:underline"
                  onClick={() => changer(FILTRES_D_ATTENTE_VIDES)}>
            Retirer les filtres
          </button>
        )}
      </div>

      {!chargement && !erreur && (
        <p className="text-sm text-gray-600">
          {total === 0 ? 'Aucun mouvement en attente ne correspond.' : (
            <>
              <strong className="tabular-nums">{total?.toLocaleString('fr-BE')}</strong> mouvement
              {(total ?? 0) > 1 ? 's' : ''} en attente, des plus anciens aux plus récents.
            </>
          )}
        </p>
      )}

      {chargement ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : erreur ? (
        <div className="rounded-lg bg-red-50 p-4">
          <p className="flex items-start gap-2 text-red-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" /> {erreur}
          </p>
        </div>
      ) : lignes.length === 0 ? (
        <div className="rounded-xl bg-white p-8 text-center shadow-md">
          <p className="text-gray-600">Rien n’attend en 499000 pour ces critères.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-white shadow-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Valeur</th>
                  <th className="px-4 py-3">Mouvement</th>
                  <th className="px-4 py-3">Pourquoi</th>
                  <th className="px-4 py-3 text-right">Montant</th>
                  <th className="px-4 py-3 text-right">En attente</th>
                  <th className="px-4 py-3">Écriture</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {lignes.map((m) => (
                  <tr key={`${m.sens}:${m.mouvementId}`} className="align-top">
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      {m.dateValeur ? dateCourte(m.dateValeur) : '—'}
                      {m.ageJours !== null && <span className="block text-xs text-gray-500">il y a {m.ageJours} j</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900">{m.contrepartie ?? (m.sens === 'entree' ? 'Entrée' : 'Sortie')}</span>
                      {m.communication && <span className="block max-w-md truncate text-xs text-gray-500" title={m.communication}>{m.communication}</span>}
                      {m.piece && <span className="block text-xs text-gray-400">{m.piece}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {m.raisons.map((r) => (
                          <span key={r} className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">{raisonAffichee(r)}</span>
                        ))}
                        {!m.decideParUnHumain && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                                title="Personne ne l’a classé hors périmètre : un reste non affecté, un classement automatique de l’import, ou une part que la base y a mise (compte client à deux tiers, facture de test, note à trancher…).">
                            à regarder
                          </span>
                        )}
                        {m.aReclasser && (
                          <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-800"
                                title="L’affectation a changé depuis l’encodage : le prochain passage du journal de trésorerie passera l’OD.">
                            reclassement au prochain passage
                          </span>
                        )}
                      </div>
                      {m.categorie && <span className="mt-1 block text-xs text-gray-500">{m.categorie}</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                      {m.sens === 'sortie' ? '−' : ''}{euros(m.montantMouvementCents)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-medium tabular-nums">{euros(m.attenteCents)}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {m.ecritureId ? (
                        <Link to={ficheEcriture(m.ecritureId)} className="font-medium text-primary-700 hover:underline">
                          {m.reference ?? m.journal ?? 'Écriture'}
                        </Link>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3">
            <p className="text-sm text-gray-700">
              Page <span className="font-medium tabular-nums">{page + 1}</span> sur{' '}
              <span className="font-medium tabular-nums">{pages}</span>
            </p>
            <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                      className={clsx('relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium',
                        page === 0 ? 'cursor-not-allowed text-gray-300' : 'text-gray-500 hover:bg-gray-50')}
                      aria-label="Page précédente">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page + 1 >= pages}
                      className={clsx('relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium',
                        page + 1 >= pages ? 'cursor-not-allowed text-gray-300' : 'text-gray-500 hover:bg-gray-50')}
                      aria-label="Page suivante">
                <ArrowRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
